import React, { useState, useRef, useEffect, useCallback, startTransition } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import io from 'socket.io-client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GoogleLogin } from '@react-oauth/google';

import CarSearchResults from './CarSearchResults';
import OfferDetails from './OfferDetails';
import { Mic, MoveUpRight, X, AlertCircle } from 'lucide-react';
import AppointmentBookingContainer from '../AppointmentBooking';
import useSessionStorage from '../../hooks/useSessionStorage';
import useAuth from '../../hooks/useAuth';
import { InfoMessage, ConfirmationModal } from '../ui';
import { PREDEFINED_PROMPTS, getTopPriorityPrompts, PROMPT_CONFIG } from '../../config/prompts';
import FeedbackForm from '../Feedback/FeedbackForm';
import feedbackService from '../../services/feedbackService';
import carnotfound from './carnotfound.png';
import IconClear from '../../icons/IconClear';

const ChatMessages = styled.div `
  flex: 1;
  overflow-y: auto;
  padding: 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem 1rem;
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 0.75rem;
    gap: 0.75rem;
  }
`;

const StartOverDivider = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #F43F60;
  font-weight: 600;
  font-size: 16px;
  opacity: 0.95;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  align-self: stretch;
  &:before,
  &:after {
    content: "";
    flex: 1;
    border-bottom: 1px dashed rgba(244, 63, 96, 0.6);
  }
`;

const MessageBubble = styled(motion.div)
`
  max-width: 85%;
  padding: 12px 15px;
  border-radius: 10px;
  line-height: 1.5;
  font-size: 1rem;
  ${props => props.isuser === "true" ? `
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    align-self: flex-end;
    border: 1px solid rgba(255, 255, 255, 0.1);
  ` : `
    background: rgba(255, 255, 255, 0.05);
    color: #ffffff;
    align-self: flex-start;
    border: 1px solid rgba(255, 255, 255, 0.1);
  `}
  
  @media (max-width: 768px) {
    max-width: 90%;
    padding: 1rem;
    font-size: 0.9rem;
    border-radius: 0.75rem;
  }
  
  @media (max-width: 480px) {
    max-width: 95%;
    padding: 0.75rem;
    font-size: 0.85rem;
    border-radius: 0.5rem;
  }

  /* Markdown styling */
  h1, h2, h3, h4, h5, h6 {
    color: #ffffff;
    margin: 0;
    font-weight: 600;
  }

  h1 { 
    font-size: 1.5rem; 
  }
  
  h2 { 
    font-size: 1.3rem; 
  }
  
  h3 { 
    font-size: 1.1rem; 
  }
  
  @media (max-width: 768px) {
    h1 { 
      font-size: 1.3rem; 
    }
    
    h2 { 
      font-size: 1.1rem; 
    }
    
    h3 { 
      font-size: 1rem; 
    }
  }
  
  @media (max-width: 480px) {
    h1 { 
      font-size: 1.2rem; 
    }
    
    h2 { 
      font-size: 1rem; 
    }
    
    h3 { 
      font-size: 0.9rem; 
    }
  }

  p {
    margin:  0;
  }

  ul, ol {
    margin: 0;
    padding-left: 0;
  }

  li {
    margin:  0;
    list-style-position: inside;
    list-style-type: disc;
    line-height: 1.3;
  }

  strong, b {
    font-weight: 600;
    color: #F43F60;
  }

  em, i {
    font-style: italic;
  }

  code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
  }

  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 0.5rem 0;
  }

  pre code {
    background: none;
    padding: 0;
  }

  blockquote {
    border-left: 3px solid #F43F60;
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.8);
  }

  a {
    color: #F43F60;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  th, td {
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 0.75rem 1rem;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: rgba(244, 63, 96, 0.2);
    font-weight: 600;
    color: #F43F60;
    text-transform: uppercase;
    font-size: 0.875rem;
    letter-spacing: 0.5px;
  }

  td {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }

  tr:nth-of-type(even) {
    background: rgba(255, 255, 255, 0.02);
  }

  tr:hover {
    background: rgba(255, 255, 255, 0.05);
    transition: background-color 0.2s ease;
  }

  /* Responsive table */
  @media (max-width: 768px) {
    table {
      font-size: 0.75rem;
    }
    
    th, td {
      word-break: break-word;
    }
    
    th {
      font-size: 0.75rem;
      letter-spacing: 0.3px;
    }
    
    td {
      font-size: 0.7rem;
    }
  }
  
  /* Extra small screens */
  @media (max-width: 480px) {
    table {
      font-size: 0.7rem;
    }
    
    th, td {
      padding: 0.3rem 0.5rem;
    }
  }
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  align-self: flex-start;
  margin-bottom: 1rem;
  .dot {
    width: 8px;
    height: 8px;
    background: #F43F60;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
  }
  .dot:nth-of-type(1) { animation-delay: 0s; }
  .dot:nth-of-type(2) { animation-delay: 0.2s; }
  .dot:nth-of-type(3) { animation-delay: 0.4s; }
  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }
`;

const CarResultsWrapper = styled(motion.div)`
  margin-top: 1rem;
  width: 100%;
`;

const InputContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 0;
  width: 100%;
`;

const InputWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const Input = styled.textarea`
    outline: none;
    background-color: rgba(255, 255, 255, 0.08);
    background-image: none;
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    -webkit-box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      box-shadow: 0 0 0 3px rgba(244, 63, 96, 0.1), 0 4px 20px rgba(0, 0, 0, 0.15);
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    color: #fff;
    display: block;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.3;
    padding: 20px 20px 20px 20px;
    position: relative;
    resize: vertical;
    width: 100%;
    min-height: 100px;
    max-height: 200px;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    border-color: #F43F60;
    font-family: inherit;

    &:focus {
      border-color: #F43F60;
      box-shadow: 0 0 0 3px rgba(244, 63, 96, 0.1), 0 4px 20px rgba(0, 0, 0, 0.15);
      background-color: rgba(255, 255, 255, 0.08);
    }
    
  
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: rgba(255, 255, 255, 0.05);
      background-color: rgba(255, 255, 255, 0.02);
    }
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
    
    @media (max-width: 768px) {
      font-size: 16px;
      min-height: 80px;
      padding: 16px;
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      min-height: 70px;
      padding: 14px;
      border-radius: 8px;
    }
`;

const SendButton = styled.button`
    background: #292929;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #404040;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
    }
    
    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
      }
      70% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
      }
      100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
      }
    }
`;

const SelectedCarAttachment = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    margin-bottom: 10px;
    gap: 10px;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    margin-bottom: 8px;
    gap: 8px;
    border-radius: 6px;
  }
`;

const CarInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CarImage = styled.img`
  width: 100px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    width: 45px;
    height: 35px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 30px;
    border-radius: 4px;
  }
`;

const CarDetails = styled.div`
  color: #ffffff;
  font-size: 16px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const CarTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const CarSubtitle = styled.div`
  opacity: 0.8;
  font-size: 12px;
`;

const CarPrice = styled.div`
  color: #4ECDC4;
  font-weight: 600;
  font-size: 16px;
  margin-top: 4px;
`;

const RemoveButton = styled.button`
  background: rgba(244, 63, 96, 0.2);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
   font-size: 30px;  
  
  &:hover {
    background: rgba(244, 63, 96, 0.3);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const OfferAttachment = styled(motion.div)`
  background: rgba(78, 205, 196, 0.1);
  border: 1px solid rgba(78, 205, 196, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const OfferInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const OfferIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #4ECDC4, #44A08D);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const OfferDetailsCard = styled.div`
  color: #ffffff;
  font-size: 16px;
  line-height: 1.4;
`;

const OfferTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: #4ECDC4;
`;

const OfferSubtitle = styled.div`
  opacity: 0.8;
  font-size: 14px;
`;

const OfferPrice = styled.div`
  color: #4ECDC4;
  font-weight: 700;
  font-size: 16px;
`;

const ModelButton = styled.a`
  display: block;
    color: #EDEDED;
    text-align: center;
    font-size: 18px;
    font-weight: 500;
    line-height: 1.2;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.50);
    padding: 16px 15px;
    width: 100%;
    
    @media (max-width: 768px) {
      font-size: 16px;
      padding: 14px 12px;
      border-radius: 10px;
    }
    
    @media (max-width: 480px) {
      font-size: 14px;
      padding: 12px 10px;
      border-radius: 8px;
    }
`;

const ChatContainer = styled.div`
  background: #000;
  border-radius: 1rem;
  padding: 15px 25px;
  
  @media (max-width: 768px) {
    padding: 12px 18px;
    border-radius: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 0.5rem;
  }
`;

const ChatActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 8px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    margin-top: 6px;
  }
`;



const CompareAttachment = styled(motion.div)`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const CompareInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CompareIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
`;

const CompareDetails = styled.div`
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
`;

const CompareTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: #F43F60;
`;

const CompareSubtitle = styled.div`
  opacity: 0.8;
  font-size: 12px;
`;

const CompareCount = styled.div`
  color: #F43F60;
  font-weight: 700;
  font-size: 16px;
`;

const CompareButton = styled.button`
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(244, 63, 96, 0.3);
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toast = styled(motion.div)`
  background: ${({ type }) => type === 'error' ? '#EF4444' : '#4ECDC4'};
  color: #ffffff;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  min-width: 300px;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 350px;
    padding: 14px 16px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    min-width: 260px;
    max-width: 320px;
    padding: 12px 14px;
    font-size: 12px;
    right: 10px;
  }
`;

const ToastIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
  line-height: 1.4;
`;

const ToastCloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Account creation form styled components
const AccountFormContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  margin: 1rem 0;
  backdrop-filter: blur(10px);
`;

const AccountFormTitle = styled.h3`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const AccountForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
`;

const FormInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
`;

const FormError = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  text-align: center;
`;

const FormSuccess = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  text-align: center;
`;

const FormButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  background: #F43F60;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #F43F60;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 1rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: #E03A56;
  }
`;



// Predefined prompts styled components
const PromptsContainer = styled(motion.div)`
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 1rem;
`;

const PromptsTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
  opacity: 0.9;
`;

const PromptsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  max-width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.4rem;
  }
`;

const PromptButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  color: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  line-height: 1.4;
  font-family: inherit;
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(244, 63, 96, 0.3);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {

    border-radius: 6px;
  }
`;

const PromptIcon = styled.span`
  display: inline-flex;
  width: 30px;
  height: 30px;
  margin-right: 0.5rem;
  background: #363636;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
`;

// Room Status Components
const RoomStatusContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  padding: 1rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const RoomStatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const RoomStatusIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #ffffff;
  
  &.pending {
    background: #f59e0b;
  }
  
  &.active {
    background: #10b981;
  }
`;

const RoomStatusText = styled.div`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
`;

const RoomStatusDetails = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;

const CancelRoomButton = styled.button`
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const AccountSuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(78, 205, 196, 0.1) 0%, rgba(78, 205, 196, 0.05) 100%);
    pointer-events: none;
  }
  
  &::after {
    content: '🎉';
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 2.5rem;
    opacity: 0.2;
    transform: rotate(15deg);
  }
`;

const AccountSuccessTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
  color: #4ECDC4;
  position: relative;
  z-index: 1;
`;

const AccountSuccessDescription = styled.div`
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  position: relative;
  z-index: 1;
`;

const AccountSuccessButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const AccountSuccessButton = styled.button`
  background: linear-gradient(45deg, #4ECDC4, #44A08D);
  color: #ffffff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-size:16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(78, 205, 196, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// OAuth styled components
const OAuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  align-items: center;
`;

const OAuthButton = styled.button`
  width: 100%;
  max-width: 300px;
  height: 48px;
  background: ${props => props.provider === 'google' ? '#4285F4' : '#000000'};
  border: 1px solid ${props => props.provider === 'google' ? '#4285F4' : '#ffffff'};
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.provider === 'google' ? '#3367D6' : '#333333'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const OAuthIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OAuthDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

// Voice Mode Indicator
const VoiceModeIndicator = styled(motion.div)`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const VoiceModeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const VoiceModeIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  animation: ${props => props.isListening ? 'voicePulse 1.5s infinite' : 'none'};
`;

const VoiceModeDetails = styled.div`
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
`;

const VoiceModeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: #F43F60;
`;

const VoiceModeSubtitle = styled.div`
  opacity: 0.8;
  font-size: 12px;
`;

const VoiceModeStopButton = styled.button`
  background: rgba(244, 63, 96, 0.2);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(244, 63, 96, 0.3);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

// Keyframes for voice animation
const voiceKeyframes = `
  @keyframes voicePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(244, 63, 96, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(244, 63, 96, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(244, 63, 96, 0);
    }
  }
`;



const HomeSearchPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [socket, setSocket] = useState(null);
  const [userId, setUserId] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingForceRender, setTypingForceRender] = useState(0);
  const messagesEndRef = useRef(null);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [isCreateAppointmentActive, setIsCreateAppointmentActive] = useSessionStorage('isCreateAppointmentActive', false);
  const [isAppointmentCreated, setIsAppointmentCreated] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [isCreateAccountActive, setIsCreateAccountActive] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [accountFormData, setAccountFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    userInfo: {
      name: '',
      phone: '',
      address: '',
      city: ''
    }
  });
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: ''
  });
  const [accountFormError, setAccountFormError] = useState('');
  const [accountFormSuccess, setAccountFormSuccess] = useState('');
  const [isAccountFormLoading, setIsAccountFormLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [roomStatus, setRoomStatus] = useState(null);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [pendingLocationMessage, setPendingLocationMessage] = useState(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const { setUserAuth } = useAuth();

  // Centralized typing indicator management
  const typingStateRef = useRef({
    isActive: false,
    timeoutId: null,
    lastMessageId: null
  });

  // Helper function to set typing indicator
  const setTypingIndicator = useCallback((messageId = null) => {
    console.log('🎤 Setting typing indicator:', { messageId, currentState: typingStateRef.current });
    
    // Clear any existing timeout
    if (typingStateRef.current.timeoutId) {
      clearTimeout(typingStateRef.current.timeoutId);
    }
    
    // Set new state
    typingStateRef.current.isActive = true;
    typingStateRef.current.lastMessageId = messageId;
    
    // Update React state and force re-render
    setIsTyping(true);
    setTypingForceRender(prev => prev + 1);
    
    // Set timeout to clear typing indicator after 30 seconds
    typingStateRef.current.timeoutId = setTimeout(() => {
      console.log('🎤 Typing indicator timeout - clearing automatically');
      clearTypingIndicator();
    }, 30000);
  }, []);

  // Helper function to clear typing indicator
  const clearTypingIndicator = useCallback(() => {
    console.log('🎤 Clearing typing indicator:', { currentState: typingStateRef.current });
    
    // Clear timeout
    if (typingStateRef.current.timeoutId) {
      clearTimeout(typingStateRef.current.timeoutId);
      typingStateRef.current.timeoutId = null;
    }
    
    // Reset state
    typingStateRef.current.isActive = false;
    typingStateRef.current.lastMessageId = null;
    
    // Update React state and force re-render
    setIsTyping(false);
    setTypingForceRender(prev => prev + 1);
  }, []);

  // Refs to maintain current values in closures
  const socketRef = useRef(socket);
  const roomIdRef = useRef(roomId);
  const selectedCarRef = useRef(selectedCar);
  const currentOfferRef = useRef(currentOffer);
  const userLocationRef = useRef(userLocation);
  const isVoiceModeRef = useRef(isVoiceMode);
  const recognitionRef = useRef(recognition);

  // Debug logging for voice mode state changes
  useEffect(() => {
    console.log('🎤 Voice mode state changed:', { isVoiceMode, isListening });
  }, [isVoiceMode, isListening]);

  // Debug logging for typing indicator changes
  useEffect(() => {
    console.log('🎤 Typing indicator state changed:', { isTyping });
  }, [isTyping]);

  // Debug logging for recognition state changes
  useEffect(() => {
    console.log('🎤 Recognition state changed:', { 
      hasRecognition: !!recognition, 
      isVoiceSupported,
      isVoiceMode,
      isListening 
    });
  }, [recognition, isVoiceSupported, isVoiceMode, isListening]);

  // Debug logging for socket and room status
  useEffect(() => {
    console.log('🎤 Socket/Room status changed:', {
      hasSocket: !!socket,
      socketConnected: socket?.connected,
      hasRoomId: !!roomId,
      roomId: roomId,
      isVoiceMode,
      isListening
    });
  }, [socket, roomId, isVoiceMode, isListening]);

  // Debug logging for component mount
  useEffect(() => {
    console.log('🎤 HomeSearchPage component mounted');
    console.log('🎤 Initial voice state:', {
      isVoiceMode,
      isListening,
      isVoiceSupported,
      hasRecognition: !!recognition
    });
  }, []);

  // Debug logging for voice mode indicator visibility
  useEffect(() => {
    if (isVoiceMode) {
      console.log('🎤 Voice mode indicator should be visible');
    } else {
      console.log('🎤 Voice mode indicator should be hidden');
    }
  }, [isVoiceMode]);

  // Keep refs updated with current values
  useEffect(() => {
    console.log('🎤 Updating socket ref:', { hasSocket: !!socket, connected: socket?.connected });
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    console.log('🎤 Updating roomId ref:', { roomId });
    roomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    console.log('🎤 Updating selectedCar ref:', { hasSelectedCar: !!selectedCar });
    selectedCarRef.current = selectedCar;
  }, [selectedCar]);

  useEffect(() => {
    console.log('🎤 Updating currentOffer ref:', { hasCurrentOffer: !!currentOffer });
    currentOfferRef.current = currentOffer;
  }, [currentOffer]);

  useEffect(() => {
    console.log('🎤 Updating userLocation ref:', { hasLocation: !!userLocation });
    userLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    console.log('🎤 Updating isVoiceMode ref:', { isVoiceMode });
    isVoiceModeRef.current = isVoiceMode;
  }, [isVoiceMode]);

  useEffect(() => {
    console.log('🎤 Updating recognition ref:', { hasRecognition: !!recognition });
    recognitionRef.current = recognition;
  }, [recognition]);

  // Helper function to get the first image from either car data structure
  const getCarImage = (car) => {
    if (!car) return carnotfound;
    
    // Try to get from images array first (selectedCar format)
    if (car.images && Array.isArray(car.images) && car.images.length > 0) {
      return car.images[0].url || carnotfound;
    }
    
    // Try to get from imagelist string (offer vehicle format) 
    if (car.imagelist) {
      try {
        let images = [];
        if (typeof car.imagelist === 'string') {
          // Try parsing as JSON first
          try {
            images = JSON.parse(car.imagelist);
          } catch {
            // If JSON parse fails, treat as CSV
            images = car.imagelist.split(',').map(url => ({ url: url.trim() }));
          }
        } else {
          images = car.imagelist;
        }
        if (Array.isArray(images) && images.length > 0) {
          return images[0].url || carnotfound;
        }
      } catch (e) {
        console.error('Error parsing imagelist:', e);
      }
    }
    
    return carnotfound;
  };

  useEffect(() => {
    const selectedCar = sessionStorage.getItem('selectedCar');
    const currentOffer = sessionStorage.getItem('currentOffer');
    if (currentOffer) {
      setCurrentOffer(JSON.parse(currentOffer));
    }
    if (selectedCar) {
      setSelectedCar(JSON.parse(selectedCar));
    }
  }, []);



  // useEffect(() => {
  //   const compareList = sessionStorage.getItem('compareList');
  //   if (compareList) {
  //     setCompareList(JSON.parse(compareList));
  //   }
  // }, []);

  // Toast functions
  const showToast = useCallback((message, type = 'error') => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      timestamp: Date.now()
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Function to handle compare list changes
  const handleCompareChange = (newCompareList) => {
    sessionStorage.setItem('compareList', JSON.stringify(newCompareList));
    setCompareList(newCompareList);
  };

  // Function to handle compare button click
  const handleCompareClick = () => {
    if (compareList.length === 0) {
      alert('No cars selected for comparison');
      return;
    }

    if (compareList.length === 1) {
      alert('Please select at least 2 cars to compare');
      return;
    }

    // Navigate to comparison page with car IDs in query parameters
    const carIds = compareList.join(',');
    window.open(`/compare?cars=${carIds}`, '_blank');
  };

  // Function to clear compare list
  const clearCompareList = () => {
    setCompareList([]);
  };

  // Function to handle opening the clear confirmation modal
  const handleClearClick = () => {
    setShowClearConfirmModal(true);
  };

  const handleNewChat = () => {
    setRoomStatus(null);
    setRoomId(null);
    sessionStorage.setItem('compareList', JSON.stringify([]));

    // // Clear session storage
    // sessionStorage.removeItem('token');
    // sessionStorage.removeItem('userId');
    sessionStorage.removeItem('selectedCar');
    sessionStorage.removeItem('currentOffer');
    setCurrentOffer(null);
    setSelectedCar(null);
    // Clear messages
    setMessages([]);
    setIsCreateAppointmentActive(false);
    setIsAppointmentCreated(false);
    setIsCreateAccountActive(false);
    setIsLoginMode(false);
    setIsAppointmentCreated(false);
    setShowFeedback(false);
    setPendingLocationMessage(null);
    setIsRequestingLocation(false);
    setIsWaitingForResponse(false);
    setAccountFormData({
      email: '',
      password: '',
      confirmPassword: '',
      userInfo: {
        name: '',
        phone: '',
        address: '',
        city: ''
      }
    });
    setLoginFormData({
      email: '',
      password: ''
    });
    setAccountFormError('');
    setAccountFormSuccess('');
    clearCompareList(); // Clear compare list when starting new chat
    // Disconnect current socket
    if (socket) {
      socket.disconnect();
    }

    // Initialize new socket connection
    const newSocket = io(process.env.REACT_APP_API_URL + '');
    setSocket(newSocket);

    const token = sessionStorage.getItem('token');
    if (token) {
      fetch(process.env.REACT_APP_API_URL + '/api/auth/update-sessionId', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success') {
            newSocket.emit('authenticate', token);
          }
        })
        .catch(err => console.error('Error updating sessionId:', err));
    }
    else {
      // Get new anonymous token
      fetch(process.env.REACT_APP_API_URL + '/api/auth/anonymous', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          console.log(data)
          if (data.status === 'success') {
            sessionStorage.setItem('token', data.data.token);
            newSocket.emit('authenticate', data.data.token);
          }
        })
        .catch(err => console.error('Error getting anonymous token:', err));
    }
  };

  const fetchMessageHistory = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/messages/history', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.status === 'success') {
        const formattedMessages = data.data.map(msg => ({
          text: msg.content,
          isUser: msg.type === 'user',
          timestamp: msg.timestamp,
          showCars: msg.carSearchResultsQuery ? true : false,
          isStartOver: msg.isStartOver ? true : false,
          cars: msg.vehicles || [],
          query: msg.carSearchResultsQuery,
          messageId: msg.messageId,
          showOffer: msg.offer ? true : false,
          offer: msg.offer || []
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error fetching message history:', error);
    }
  };

  // Function to request location permission
  const requestLocationPermission = useCallback((messageData = null) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }

    setIsRequestingLocation(true);
    
      navigator.geolocation.getCurrentPosition(
        (position) => {
        const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
        };
        
        setUserLocation(newLocation);
        setIsRequestingLocation(false);
        
        // If there's a pending message that needs location data, re-send it
        if (messageData) {
          const updatedMessageData = {
            ...messageData,
            latitude: newLocation.latitude,
            longitude: newLocation.longitude
          };
          
          if (socket && roomId) {
            socket.emit('chatMessage', updatedMessageData);
            setTypingIndicator(`regular_${Date.now()}`);
            setIsWaitingForResponse(true); // Set waiting state when sending pending message
            
            // Show success toast
            // showToast('Location permission granted! Message sent with location data.', 'success');
          }
          
          setPendingLocationMessage(null);
        } else {
          // showToast('Location permission granted!', 'success');
        }
        },
        (error) => {
          console.error('Error getting location:', error);
        setIsRequestingLocation(false);
        
        if (messageData) {
          // If location was denied, send message without location
          if (socket && roomId) {
            socket.emit('chatMessage', messageData);
            setTypingIndicator(`regular_${Date.now()}`);
            setIsWaitingForResponse(true); // Set waiting state when sending without location
            showToast('Message sent without location data.', 'error');
          }
          setPendingLocationMessage(null);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }, [socket, roomId, showToast]);

  // Get user location on component mount
  useEffect(() => {
    requestLocationPermission();
  }, [requestLocationPermission]);

  // Initialize speech recognition
  useEffect(() => {
    console.log('🎤 Initializing speech recognition...');
    
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      console.log('🎤 Speech recognition supported, configuring...');
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = false;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onstart = () => {
        console.log('🎤 Speech recognition started');
        console.log('🎤 Voice mode state:', { isVoiceMode, isListening });
        setIsListening(true);
        setIsVoiceMode(true);
        console.log('🎤 Updated states - isListening: true, isVoiceMode: true');
      };
      
      recognitionInstance.onresult = (event) => {
        console.log('🎤 Speech recognition result received:', {
          resultsLength: event.results.length,
          isFinal: event.results[event.results.length - 1].isFinal,
          confidence: event.results[event.results.length - 1][0].confidence
        });
        
        const transcript = event.results[event.results.length - 1][0].transcript;
        console.log('🎤 Raw transcript:', `"${transcript}"`);
        console.log('🎤 Trimmed transcript:', `"${transcript.trim()}"`);
        console.log('🎤 Transcript length:', transcript.trim().length);
        
        // Only process if we have a meaningful transcript
        if (transcript.trim().length > 0) {
          console.log('🎤 Processing meaningful transcript, adding to input...');
          
          // Add transcript to input field instead of auto-sending
          setInput(transcript.trim());
          
          // Stop recording after getting the transcript
          console.log('🎤 Stopping recognition after transcript received...');
          recognitionInstance.stop();
        } else {
          console.log('🎤 Skipping empty transcript');
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('🎤 Speech recognition error:', {
          error: event.error,
          message: event.message,
          timeStamp: event.timeStamp,
          isVoiceMode,
          isListening
        });
        
        // Only stop voice mode for certain errors
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          console.log('🎤 Permission denied error - stopping voice mode');
          setIsListening(false);
          setIsVoiceMode(false);
          showToast('Microphone permission denied. Please allow microphone access.', 'error');
        } else if (event.error === 'no-speech') {
          console.log('🎤 No speech detected - continuing to listen');
          // Don't stop for no-speech, just show a brief message
          showToast('No speech detected.', 'error');
        } else if (event.error === 'network') {
          console.log('🎤 Network error - attempting to continue');
          showToast('Network error. Trying to reconnect...', 'error');
        } else {
          console.log('🎤 Other error - attempting restart in 1 second');
        // For other errors, try to restart
        setTimeout(() => {
          console.log('🎤 Attempting to restart recognition after error...');
          if (isVoiceModeRef.current && recognitionRef.current) {
            try {
              console.log('🎤 Restarting recognition...');
              recognitionRef.current.start();
              console.log('🎤 Recognition restart successful');
            } catch (e) {
              console.error('🎤 Failed to restart recognition:', e);
              setIsListening(false);
              setIsVoiceMode(false);
              console.log('🎤 Voice mode stopped due to restart failure');
            }
          } else {
            console.log('🎤 Not restarting - voice mode inactive or recognition not available');
          }
        }, 1000);
        }
      };
      
      recognitionInstance.onend = () => {
        console.log('🎤 Speech recognition ended');
        console.log('🎤 Current voice mode state (using ref):', { isVoiceMode: isVoiceModeRef.current, isListening });
        setIsListening(false);
        
        // Stop voice mode after each recording session
        console.log('🎤 Stopping voice mode after recording session...');
        setIsVoiceMode(false);
      };
      
      setRecognition(recognitionInstance);
      setIsVoiceSupported(true);
      console.log('🎤 Speech recognition initialized successfully');
    } else {
      console.warn('🎤 Speech recognition not supported in this browser');
      setIsVoiceSupported(false);
    }
  }, [showToast]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      console.log('🎤 Component unmounting - cleaning up speech recognition...');
      if (recognition) {
        console.log('🎤 Stopping recognition...');
        recognition.stop();
        console.log('🎤 Recognition stopped');
      }
      setIsVoiceMode(false);
      console.log('🎤 Voice mode set to false');
      
      // Clear typing indicator
      clearTypingIndicator();
    };
  }, [recognition]);

  // Check room status when roomId changes and set up periodic checks
  useEffect(() => {
    if (roomId && socket) {
      checkRoomStatus();

      // Set up periodic room status check every 5 seconds
      const intervalId = setInterval(() => {
        checkRoomStatus();
      }, 5000);

      // Cleanup interval on unmount or when roomId/socket changes
      return () => clearInterval(intervalId);
    }
  }, [roomId, socket]);

  const generateRoomId = () => {
    const timestamp = Date.now().toString();
    const randomNum = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    return `room_${timestamp}_${randomNum}`;
  };
  useEffect(() => {
    let newSocket;
    if (!socket) {
      newSocket = io(process.env.REACT_APP_API_URL + '');
      setSocket(newSocket);
    } else {
      const token = sessionStorage.getItem('token');
      if (!token) {
        fetch(process.env.REACT_APP_API_URL + '/api/auth/anonymous', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
          .then(res => res.json())
          .then(data => {
            console.log(data)

            if (data.status === 'success') {
              console.log("test")
              sessionStorage.setItem('token', data.data.token);
              sessionStorage.setItem('role', 'anonymous');
              socket.emit('authenticate', data.data.token);
            }
          })
          .catch(err => console.error('Error getting anonymous token:', err));
      } else {
        socket.emit('authenticate', token);
      }
      socket.on('authenticated', (data) => {
        // showToast( 'Authentication successful. Connected to the chat.', 'success');

        setUserId(data.userId);
        setRoomId(data.roomId);
        sessionStorage.setItem('userId', data.userId);
        fetchMessageHistory();
      });
      socket.on('dealer_pending', (data) => {

        socket.emit('joinRoom', data.roomId)
        checkRoomStatus()
      });
      socket.on('dealerJoined', (data) => {


        // Check room status after dealer joins
        if (roomId) {
          socket.emit('checkRoomStatus', roomId);
        }
      });
      socket.on('chatMessage', (message) => {
        setMessages(prev => [...prev, {
          text: message.content,
          isUser: message.type === 'user',
          timestamp: message.timestamp
        }]);
        if (message.type === 'bot') {
          clearTypingIndicator();
          setIsWaitingForResponse(false); // Enable sending when bot responds
        }
      });
      socket.on('create_appointment', (data) => {
        setIsCreateAppointmentActive(true);
      });

      socket.on('CREATE_ACCOUNT', (data) => {
        setIsCreateAccountActive(true);
        clearTypingIndicator();

      });

      socket.on('carSearchResult', (data) => {
        {
          setMessages(prev => [...prev, {
            text: "Here are some cars that match your criteria:",
            isUser: false,
            showCars: true,
            cars: data.vehicles,
            messageId: data.messageId
          }]);
        }
        clearTypingIndicator();
        setIsWaitingForResponse(false); // Enable sending when car results are shown
      });

      socket.on('waiting_for_dealer', (data) => {

      });
      socket.on('offer', (data) => {
        sessionStorage.setItem('currentOffer', JSON.stringify(data));
        setCurrentOffer(data);
        
        // Update selectedCar to match the offer's vehicle data for consistency
        if (data.vehicle) {
          setSelectedCar(data.vehicle);
          sessionStorage.setItem('selectedCar', JSON.stringify(data.vehicle));
        }
        
        setMessages(prev => [...prev, {
          text: "Here is the offer for the car you selected:",
          isUser: false,
          showOffer: true,
          offer: data
        }]);
        clearTypingIndicator();
        setIsWaitingForResponse(false); // Enable sending when offer is shown
      });
      socket.on('account_created', async (data) => {
        console.log('Account created event received:', data);

        const token = sessionStorage.getItem('token');
        sessionStorage.setItem('role', data.user.role);
        sessionStorage.setItem('userId', data.user.id);
        // Store user data using the auth hook
        setUserAuth(data.user, token);

        // Add success message to chat
        setMessages(prev => [...prev, {
          text: "Your account has been created successfully! 🎉",
          isUser: false,
          timestamp: new Date().toISOString(),
          showAccountSuccessCard: true,
          isLoginMode: false
        }]);

        // Close the account creation form
        setIsCreateAccountActive(false);
        setIsAccountFormLoading(false);
        setAccountFormSuccess('');
        setAccountFormError('');

        // Reset form data
        setAccountFormData({
          email: '',
          password: '',
          confirmPassword: '',
          userInfo: {
            name: '',
            phone: '',
            address: '',
            city: ''
          }
        });

        clearTypingIndicator();
        setIsWaitingForResponse(false); // Enable sending when account is created
      });

      socket.on('offer_updated', (data) => {

        // Search for the offer in messages and update its status
        setMessages(prev => prev.map(message => {
          if (message.showOffer && message.offer?.offer?.id === data.offer.id) {
            return {
              ...message,
              offer: {
                ...message.offer,
                offer: {
                  ...data.offer
                }
              }
            };
          }
          return message;
        }));

        clearTypingIndicator();
        setIsWaitingForResponse(false); // Enable sending when offer is updated
      });

      // Handle room status updates
      socket.on('roomStatus', (data) => {
        setRoomStatus(data);
      });

      socket.on('error', (data) => {
        console.log(data)
        // Show error toast with the message from the server
        if(data.message == "Please authenticate first") {
          showToast( 'Authentication failed. Trying to reconnect...', 'error');

          const token = sessionStorage.getItem('token');
          if (!token) {
            fetch(process.env.REACT_APP_API_URL + '/api/auth/anonymous', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            })
              .then(res => res.json())
              .then(data => {
                console.log(data)
    
                if (data.status === 'success') {
                  console.log("test")
                  sessionStorage.setItem('token', data.data.token);
                  sessionStorage.setItem('role', 'anonymous');
                  socket.emit('authenticate', data.data.token);
                }
              })
              .catch(err => console.error('Error getting anonymous token:', err));
          } else {
            socket.emit('authenticate', token);
          }
        } else {
          showToast(data.message || 'An error occurred', 'error');
        }
        clearTypingIndicator();
        setIsAccountFormLoading(false);
        setIsWaitingForResponse(false); // Enable sending when error occurs
      });

      // Login event handlers
      socket.on('login_success', (data) => {
        console.log('Login successful:', data);

        if (data.status === 'success' && data.data) {
          // Store token and user data in sessionStorage (same as UserLogin.js)
          sessionStorage.setItem('token', data.data.token);
          sessionStorage.setItem('user', JSON.stringify(data.data.user));
          sessionStorage.setItem('role', data.data.user.role);
          sessionStorage.setItem('userId', data.data.user.id);

          // Update auth state
          setUserAuth(data.data.user, data.data.token);

          // Add success message to chat
          setMessages(prev => [...prev, {
            text: `Welcome back, ${data.data.user.name}! You are now logged in. 🎉`,
            isUser: false,
            timestamp: new Date().toISOString(),
            showAccountSuccessCard: true,
            isLoginMode: true

          }]);

          // Close the login form
          setIsCreateAccountActive(false);
          setIsLoginMode(false);
          setIsAccountFormLoading(false);
          setAccountFormSuccess('');
          setAccountFormError('');

          // Reset form data
          setLoginFormData({
            email: '',
            password: ''
          });

          clearTypingIndicator();
          setIsWaitingForResponse(false); // Enable sending when login is successful

          // Show success toast
          showToast('Login successful!', 'success');
        }
      });

      socket.on('login_error', (data) => {
        console.log('Login error:', data);
        setAccountFormError(data.message || 'Login failed. Please try again.');
        setIsAccountFormLoading(false);
        setAccountFormSuccess('');
        setIsWaitingForResponse(false); // Enable sending when login fails
      });

      // Handle start over event - clear selected car and active offer
      socket.on('start_over', (data) => {
        console.log('Start over event received:', data);
        setSelectedCar(null);
        setCurrentOffer(null);
        sessionStorage.removeItem('selectedCar');
        sessionStorage.removeItem('currentOffer');
      });

      return () => { socket.disconnect(); };
    }
  }, [socket]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

    }
  };

  // Only scroll when messages change, not on every render
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length]);

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  // Handle voice recording toggle
  const handleVoiceInput = useCallback(() => {
    console.log('🎤 Voice input button clicked');
    console.log('🎤 Current state:', { 
      isVoiceMode, 
      isListening, 
      hasRecognition: !!recognition,
      isVoiceSupported 
    });

    if (!recognition) {
      console.warn('🎤 No recognition available - showing error toast');
      showToast('Voice recognition not supported in this browser', 'error');
      return;
    }

    if (isVoiceMode) {
      console.log('🎤 Stopping voice mode...');
      // Stop voice mode
      setIsVoiceMode(false);
      if (recognition && isListening) {
        console.log('🎤 Stopping recognition...');
        recognition.stop();
        console.log('🎤 Recognition stop called');
      }
      setIsListening(false);
      console.log('🎤 Voice mode stopped - isVoiceMode: false, isListening: false');
    } else {
      console.log('🎤 Starting voice mode...');
      // Start voice mode
      try {
        console.log('🎤 Calling recognition.start()...');
        recognition.start();
        console.log('🎤 Recognition start called successfully');
      } catch (error) {
        console.error('🎤 Error starting speech recognition:', error);
        showToast('Failed to start voice recording. Please try again.', 'error');
      }
    }
  }, [recognition, isVoiceMode, isListening, showToast]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim() && !selectedCar) return;
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
    const messageData = {
        roomId: roomId,
        message: input,
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
    };

    // Check if the message might benefit from location data
    const needsLocation = input.toLowerCase().includes('near me') || 
                         input.toLowerCase().includes('nearby') || 
                         input.toLowerCase().includes('local') ||
                         input.toLowerCase().includes('location') ||
                         input.toLowerCase().includes('distance');

    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      if (userLocation || !needsLocation) {
        // Send immediately if we have location or don't need it
        socket.emit('chatMessage', messageData);
        setTypingIndicator(`regular_${Date.now()}`);
      } else {
        // Store the message and request location permission
        setPendingLocationMessage(messageData);
        showToast('Requesting location permission to provide better results...', 'success');
        requestLocationPermission(messageData);
      }
    }
    setInput('');
  };

  // Handle key press in textarea
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
    // Shift+Enter will create a new line (default textarea behavior)
  };

  const handleCarSelect = (car) => {
    setSelectedCar(car);
    sessionStorage.setItem('selectedCar', JSON.stringify(car));
    if (!car) return;
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
    const messageData = {
        roomId: roomId,
        message: `I'm interested in the ${car.year} ${car.make} ${car.model} `,
        selectedCar: car,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
    };

    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      if (userLocation) {
        // Send immediately if we have location
        socket.emit('chatMessage', messageData);
        setTypingIndicator(`regular_${Date.now()}`);
      } else {
        // Request location permission for better car recommendations
        setPendingLocationMessage(messageData);
        showToast('Requesting location permission for better car recommendations...', 'success');
        requestLocationPermission(messageData);
      }
    }
    // Scroll after car selection
    setTimeout(() => scrollToBottom(), 100);
  };
  const handleCarSelectWithOffer = (car, offerPrice) => {
    setSelectedCar(car);
    sessionStorage.setItem('selectedCar', JSON.stringify(car));
    if (!car) return;
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
    const messageData = {
        roomId: roomId,
        message: `I would like to make an offer of $${offerPrice} for the ${car.year} ${car.make} ${car.model}`,
        selectedCar: car,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
    };

    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      if (userLocation) {
        // Send immediately if we have location
        socket.emit('chatMessage', messageData);
        setTypingIndicator(`regular_${Date.now()}`);
      } else {
        // Request location permission for better car recommendations
        setPendingLocationMessage(messageData);
        showToast('Requesting location permission for better car recommendations...', 'success');
        requestLocationPermission(messageData);
      }
    }
    // Scroll after car selection
    setTimeout(() => scrollToBottom(), 100);
  };
  const removeSelectedCar = () => { setSelectedCar(null); sessionStorage.removeItem('selectedCar'); };

  const removeCurrentOffer = () => { setCurrentOffer(null); sessionStorage.removeItem('currentOffer'); };

  const handleOfferAccept = (offerData) => {
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
    sessionStorage.setItem('currentOffer', JSON.stringify(offerData));
    setCurrentOffer(offerData);
    
    // Update selectedCar to match the offer's vehicle data for consistency
    if (offerData.vehicle) {
      setSelectedCar(offerData.vehicle);
      sessionStorage.setItem('selectedCar', JSON.stringify(offerData.vehicle));
    }
    
    if (!input.trim() && !selectedCar && !offerData?.offer?.id) return;
    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: "I accept the offer",
        selectedCar: offerData.vehicle || selectedCar,
        offerId: offerData.offer.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
    setInput('');
  };

  const handleOfferDecline = (offerData) => {
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
    sessionStorage.setItem('currentOffer', JSON.stringify(offerData));
    setCurrentOffer(offerData);
    
    // Update selectedCar to match the offer's vehicle data for consistency
    if (offerData.vehicle) {
      setSelectedCar(offerData.vehicle);
      sessionStorage.setItem('selectedCar', JSON.stringify(offerData.vehicle));
    }
    
    if (!input.trim() && !selectedCar && !offerData?.offer?.id) return;
    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: "I decline the offer",
        selectedCar: offerData.vehicle || selectedCar,
        offerId: offerData.offer.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
    setInput('');
  };

  const handleQuickResponse = (offerData, response) => {
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
    console.log(offerData, response)
    sessionStorage.setItem('currentOffer', JSON.stringify(offerData));
    setCurrentOffer(offerData);
    
    // Update selectedCar to match the offer's vehicle data for consistency
    if (offerData.vehicle) {
      setSelectedCar(offerData.vehicle);
      sessionStorage.setItem('selectedCar', JSON.stringify(offerData.vehicle));
    }
    
    if (!input.trim() && !offerData?.offer?.id) return;
    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: response,
        selectedCar: offerData.vehicle,
        offerId: offerData.offer.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
    setInput('');
  };

  // Handle predefined prompt selection
  const handlePromptSelect = (promptText) => {
    if (isWaitingForResponse) return; // Prevent sending if waiting for response
    
    if (socket && roomId) {
      // Set waiting state immediately when sending
      setIsWaitingForResponse(true);
      
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: promptText,
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
  };

  // Account creation form handlers
  const handleAccountFormInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setAccountFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setAccountFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (accountFormError) setAccountFormError('');
  };

  const handleAccountFormSubmit = async () => {
    console.log('Account form submit handler called');
    setIsAccountFormLoading(true);
    setAccountFormError('');
    setAccountFormSuccess('');

    // Validate passwords match
    if (accountFormData.password !== accountFormData.confirmPassword) {
      setAccountFormError('Passwords do not match');
      setIsAccountFormLoading(false);
      return;
    }

    // Validate password length
    if (accountFormData.password.length < 6) {
      setAccountFormError('Password must be at least 6 characters long');
      setIsAccountFormLoading(false);
      return;
    }

    // Send account creation request via socket
    if (socket && roomId) {
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('createAccount', {
        roomId: roomId,
        email: accountFormData.email,
        password: accountFormData.password,
        userInfo: accountFormData.userInfo,
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });

      setAccountFormSuccess('Creating your account...');
    } else {
      setAccountFormError('Connection error. Please try again.');
      setIsAccountFormLoading(false);
    }
  };

  const handleAccountFormCancel = () => {
    setIsCreateAccountActive(false);
    setIsLoginMode(false);
    setAccountFormData({
      email: '',
      password: '',
      confirmPassword: '',
      userInfo: {
        name: '',
        phone: '',
        address: '',
        city: ''
      }
    });
    setLoginFormData({
      email: '',
      password: ''
    });
    setAccountFormError('');
    setAccountFormSuccess('');
  };

  // Login form handlers
  const handleLoginFormInputChange = (e) => {
    const { name, value } = e.target;
    setLoginFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (accountFormError) setAccountFormError('');
  };

  const handleLoginFormSubmit = () => {
    console.log('Login form submit handler called');
    setIsAccountFormLoading(true);
    setAccountFormError('');
    setAccountFormSuccess('');

    // Validate form data
    if (!loginFormData.email || !loginFormData.password) {
      setAccountFormError('Please fill in all fields');
      setIsAccountFormLoading(false);
      return;
    }

    // Send login request via socket
    if (socket && roomId) {
      socket.emit('loginUser', {
        roomId: roomId,
        email: loginFormData.email,
        password: loginFormData.password,
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude
      });

      setAccountFormSuccess('Logging you in...');
    } else {
      setAccountFormError('Connection error. Please try again.');
      setIsAccountFormLoading(false);
    }
  };

  const toggleLoginMode = () => {
    setIsLoginMode(!isLoginMode);
    setAccountFormError('');
    setAccountFormSuccess('');
  };

  // OAuth handlers
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsOAuthLoading(true);
    setAccountFormError('');
    setAccountFormSuccess('');

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: credentialResponse.credential,
          userInfo: {}
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        // Store token and user data
        sessionStorage.setItem('token', data.data.token);
        sessionStorage.setItem('user', JSON.stringify(data.data.user));
        sessionStorage.setItem('role', data.data.user.role);
        sessionStorage.setItem('userId', data.data.user.id);

        // Update auth state
        setUserAuth(data.data.user, data.data.token);

        // Add success message to chat
        setMessages(prev => [...prev, {
          text: `Welcome back, ${data.data.user.name}! You are now logged in with Google. 🎉`,
          isUser: false,
          timestamp: new Date().toISOString(),
          showAccountSuccessCard: true,
          isLoginMode: true
        }]);

        // Close the account creation form
        setIsCreateAccountActive(false);
        setIsLoginMode(false);
        setAccountFormSuccess('');
        setAccountFormError('');

        showToast('Google login successful!', 'success');
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setAccountFormError(error.message || 'Google login failed. Please try again.');
      showToast('Google login failed', 'error');
    } finally {
      setIsOAuthLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google login error:', error);
    setAccountFormError('Google login failed. Please try again.');
    showToast('Google login failed', 'error');
  };

 

  // Feedback handlers
  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      // Add user location to feedback data
      const enrichedFeedbackData = {
        ...feedbackData,
        userLocation: userLocation
      };

      await feedbackService.submitFeedback(enrichedFeedbackData);
      showToast('Feedback submitted successfully!', 'success');

      // Clear chat and create new session after successful feedback submission
      setTimeout(() => {
        handleNewChat();
      }, 2000); // Wait 2 seconds to show the success message before clearing

    } catch (err) {
      console.error('Feedback submission error:', err);
      showToast('Failed to submit feedback. Please try again.', 'error');
      throw err; // Re-throw to let the FeedbackForm handle the error
    }
  };

  const handleFeedbackSkip = () => {

    setShowFeedback(false);

    // Clear chat and create new session after skipping feedback
    setTimeout(() => {
      handleNewChat();
    }, 500); // Short delay to ensure the feedback form is hidden first
  };

  // Check room status
  const checkRoomStatus = () => {
    if (socket && roomId) {
      socket.emit('checkRoomStatus', roomId);
    }
  };

  // Cancel room
  const handleCancelRoom = () => {
    if (socket && roomId) {
      socket.emit('cancelRoom', roomId);
      setRoomStatus(null);
    }
  };

  // Account success card handlers
  const handleScheduleTestDrive = () => {
    if (socket && roomId) {
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: "I would like to schedule a test drive",
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
  };

  const handleCheckFinancing = () => {
    if (socket && roomId) {
      const state = sessionStorage.getItem('stateCode');
      const postalCode = sessionStorage.getItem('postalCode');
      socket.emit('chatMessage', {
        roomId: roomId,
        message: "I would like to check financing options",
        selectedCar: selectedCar,
        offerId: currentOffer?.offer?.id,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        state: state || null,
        postalCode: postalCode || null
      });
      setTypingIndicator(`regular_${Date.now()}`);
    }
  };

  // Function to trigger feedback after appointment scheduling
  const triggerFeedbackAfterAppointment = useCallback((e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log("triggerFeedbackAfterAppointment");
    if (!showFeedback) {
      // Add a delay to let the appointment success message appear
      setShowFeedback(true);
    }
  }, [showFeedback]);

  // --- UI ---
  return (
    <>
      {/* CSS Keyframes for voice animations */}
      <style>{voiceKeyframes}</style>

      {/* Toast Container */}
      <ToastContainer>
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              type={toast.type}
              initial={{ opacity: 0, x: 300, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 300, scale: 0.8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <ToastIcon>
                <AlertCircle size={20} />
              </ToastIcon>
              <ToastContent>
                {toast.message}
              </ToastContent>
              <ToastCloseButton onClick={() => removeToast(toast.id)}>
                <X size={16} />
              </ToastCloseButton>
            </Toast>
          ))}
        </AnimatePresence>
      </ToastContainer>

      {/* Loader */}
      {/* <div className="loaders">
        <div className="loader"></div>
      </div> */}
      {/* Header - custom class names to avoid CSS conflicts */}

      {/* Main Content */}
      <main className="main-content">
        <section className="search-area home" style={{ 
        }}>
          <div className="containers">
            <div className="row justify-content-center" >
              <div className="col-md-12 col-lg-10 col-xl-10" >
                <div className="title" style={{
                  '@media (max-width: 768px)': {
                    textAlign: 'center',
                    marginBottom: '1rem'
                  },
                  '@media (max-width: 480px)': {
                    marginBottom: '0.75rem'
                  }
                }}>
                  <p style={{
                    '@media (max-width: 768px)': {
                      fontSize: '14px',
                      marginBottom: '0.5rem'
                    },
                    '@media (max-width: 480px)': {
                      fontSize: '12px'
                    }
                  }}>Your Car Shopping AI Assistant </p>
                  <h1 style={{
                    '@media (max-width: 768px)': {
                      fontSize: '28px',
                      lineHeight: '1.2'
                    },
                    '@media (max-width: 480px)': {
                      fontSize: '24px'
                    }
                  }}>What <span>Car</span> Are You Looking For?</h1>
                </div>

                <div className="prompt-clearbtn" style={{
                  '@media (max-width: 768px)': {
                    textAlign: 'center',
                    marginBottom: '1rem'
                  }
                }}>
                  <button onClick={handleClearClick} aria-label="Clear chat" style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    '@media (max-width: 768px)': {
                      fontSize: '14px',
                      padding: '8px 12px',
                      borderRadius: '6px'
                    },
                    '@media (max-width: 480px)': {
                      fontSize: '12px',
                      padding: '6px 10px'
                    }
                  }}>
                    Clear
                    <IconClear width={16} height={16} style={{ transform: 'scaleX(-1)' }} />
                  </button>
                </div>



                <ChatContainer>
                  {/* Show predefined prompts when no messages exist */}
                  {messages.length === 0 && (
                    <PromptsContainer
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <PromptsTitle>Quick Start - Choose a prompt to begin:</PromptsTitle>
                      <PromptsGrid>
                        {PREDEFINED_PROMPTS.map((prompt) => (
                          <PromptButton
                            key={prompt.id}
                            onClick={() => handlePromptSelect(prompt.text)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: prompt.id * PROMPT_CONFIG.animationDelay }}
                            aria-label={`Use prompt: ${prompt.text}`}
                            title={prompt.text}
                          >
                            <PromptIcon>{prompt.icon}</PromptIcon>
                            {prompt.text}
                          </PromptButton>
                        ))}
                      </PromptsGrid>
                    </PromptsContainer>
                  )}



                  {/* Show chat messages when they exist */}
                  {messages.length > 0 && (
                    <ChatMessages>
                      <AnimatePresence>
                        {messages.map((message, index) => (
                          
                          <React.Fragment key={index}>
                            {message?.isStartOver && (
                              <StartOverDivider>Start Over</StartOverDivider>
                            )}
                           <MessageBubble
                              isuser={message.isUser.toString()}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div style={{whiteSpace:"pre-wrap"}}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>

                                </div>
                            </MessageBubble>
                            
 
                            {message.showAccountSuccessCard && (
                              <AccountSuccessCard
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                              >
                                <AccountSuccessTitle>
                                  {message.isLoginMode ? "Logged In Successfully! 🎉" : "Account Created Successfully! 🎉"}
                                </AccountSuccessTitle>
                                <AccountSuccessDescription>
                                  Your account has been created successfully!
                                  <br />
                                  Would you like to schedule a test drive or check financing options?
                                </AccountSuccessDescription>
                                <AccountSuccessButtons>
                                  <AccountSuccessButton onClick={handleScheduleTestDrive}>
                                    Schedule Test Drive
                                  </AccountSuccessButton>
                                  <AccountSuccessButton onClick={handleCheckFinancing}>
                                    Check Financing
                                  </AccountSuccessButton>
                                </AccountSuccessButtons>
                              </AccountSuccessCard>
                            )}

                            {message.showCars && (
                              <CarResultsWrapper
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.2 }}
                              >
                                <CarSearchResults
                                  setSelectedCara={setSelectedCar}
                                  messageId={message.messageId}
                                  cars={message.cars || []}
                                  onCarSelect={handleCarSelect}
                                  onCarSelectWithOffer={handleCarSelectWithOffer}
                                  compareList={compareList}
                                  onCompareChange={handleCompareChange}
                                  onCompareClick={handleCompareClick}
                                  onFreshSearch={handlePromptSelect}
                                />
                              </CarResultsWrapper>
                            )}
                            {message.showOffer && (
                              <OfferDetails
                                offerData={message.offer}
                                onAccept={handleOfferAccept}
                                onDecline={handleOfferDecline}
                                onQuickResponse={handleQuickResponse}
                              />
                            )}
                          </React.Fragment>
                        ))}

                        {/* Typing Indicator - Only show one after the last message */}
                        {typingStateRef.current.isActive && typingForceRender > 0 && (
                          <TypingIndicator
                            key="typing-indicator"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="dot" />
                            <div className="dot" />
                            <div className="dot" />
                          </TypingIndicator>
                        )}

                         {isRequestingLocation && (
                           <MessageBubble
                             isuser="false"
                             initial={{ opacity: 0, y: 20 }}
                             animate={{ opacity: 1, y: 0 }}
                             exit={{ opacity: 0, y: -20 }}
                             transition={{ duration: 0.3 }}
                           >
                             <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                               <div className="dot" style={{ animation: 'typing 1.4s infinite ease-in-out' }} />
                               <div className="dot" style={{ animation: 'typing 1.4s infinite ease-in-out', animationDelay: '0.2s' }} />
                               <div className="dot" style={{ animation: 'typing 1.4s infinite ease-in-out', animationDelay: '0.4s' }} />
                               <span>Requesting location permission...</span>
                             </div>
                           </MessageBubble>
                         )}

                        {/* Voice Mode Indicator */}
                        <AnimatePresence>
                          {isVoiceMode && (
                            <VoiceModeIndicator
                              key="voice-mode-indicator"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              onAnimationStart={() => console.log('🎤 Voice mode indicator appearing...')}
                              onAnimationComplete={() => console.log('🎤 Voice mode indicator appeared')}
                            >
                             <VoiceModeInfo>
                               <VoiceModeIcon isListening={isListening}>
                                 <Mic size={20} />
                               </VoiceModeIcon>
                               <VoiceModeDetails>
                                 <VoiceModeTitle>
                                   {isListening ? 'Listening...' : 'Voice Mode Active'}
                                 </VoiceModeTitle>
                                 <VoiceModeSubtitle>
                                   {isListening 
                                     ? 'Speak naturally - text will appear in input field' 
                                     : 'Voice mode is active. Click to stop or speak to continue.'
                                   }
                                 </VoiceModeSubtitle>
                               </VoiceModeDetails>
                             </VoiceModeInfo>
                             <VoiceModeStopButton 
                               onClick={() => {
                                 console.log('🎤 Stop button clicked in voice mode indicator');
                                 handleVoiceInput();
                               }}
                             >
                               Stop
                             </VoiceModeStopButton>
                            </VoiceModeIndicator>
                          )}
                        </AnimatePresence>
                        {/* {currentOffer && (
                                    <OfferDetails
                                      offerData={currentOffer}
                                      onAccept={handleOfferAccept}
                                      onDecline={handleOfferDecline}
                                      onQuickResponse={handleQuickResponse}
                                    />
                                  )} */}

                      </AnimatePresence>

                      {/* Room Status Display */}
                    </ChatMessages>
                  )
                  }


                  {/* Room Status Display */}
                  {roomStatus && (roomStatus.status === 'pending' || roomStatus.status === 'active') && (
                    <RoomStatusContainer
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <RoomStatusInfo>
                        <RoomStatusIcon className={roomStatus.dealerConnected ? 'active' : roomStatus.status}>
                          {roomStatus.dealerConnected ? '✅' : roomStatus.status === 'pending' ? '⏳' : '✅'}
                        </RoomStatusIcon>
                        <div>
                          <RoomStatusText>
                            {roomStatus.dealerConnected
                              ? 'Dealer Connected'
                              : roomStatus.status === 'pending'
                                ? 'Waiting for dealer...'
                                : 'Room Ready'
                            }
                          </RoomStatusText>
                          <RoomStatusDetails>
                            {roomStatus.dealerConnected
                              ? 'A dealer is currently in your chat room'
                              : roomStatus.status === 'pending'
                                ? 'Dealer has not entered chat yet'
                                : 'Room is ready and dealer can join'
                            }
                          </RoomStatusDetails>
                        </div>
                      </RoomStatusInfo>
                      {(roomStatus.status === 'active' || roomStatus.dealerConnected) && (
                        <CancelRoomButton type="button" onClick={handleCancelRoom}>
                          End Chat
                        </CancelRoomButton>
                      )}
                    </RoomStatusContainer>
                  )}

                  <form onSubmit={isCreateAccountActive ? (e) => e.preventDefault() : handleSend}>

                    {selectedCar && (
                      <SelectedCarAttachment
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <CarInfo>
                          <CarImage
                            src={getCarImage(selectedCar)}
                            alt={selectedCar.make + ' ' + selectedCar.model}
                            onError={(e) => {
                              e.target.src = carnotfound;
                            }}
                          />
                          <CarDetails>
                            <CarTitle>{selectedCar.year} {selectedCar.make} {selectedCar.model}</CarTitle>
                            <CarSubtitle>{selectedCar.trim} • {selectedCar.mileage} miles</CarSubtitle>
                            <CarPrice>
                              {!selectedCar.price || selectedCar.price === 0 ? (
                                <span style={{ color: '#4ECDC4', fontStyle: 'italic' }}>Not Priced Yet</span>
                              ) : (
                                `$${selectedCar.price?.toLocaleString()}`
                              )}
                            </CarPrice>
                          </CarDetails>
                        </CarInfo>
                        <RemoveButton onClick={removeSelectedCar} type="button">
                          ×
                        </RemoveButton>
                      </SelectedCarAttachment>
                    )}

                    {currentOffer && (
                      <OfferAttachment
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <OfferInfo>
                          <OfferIcon>
                            $
                          </OfferIcon>
                          <OfferDetailsCard>
                            <OfferTitle>Active Offer</OfferTitle>
                            <OfferSubtitle>
                              {currentOffer.vehicle?.year} {currentOffer.vehicle?.make} {currentOffer.vehicle?.model}
                            </OfferSubtitle>
                          </OfferDetailsCard>
                          <OfferPrice>
                            ${currentOffer.offer?.amount?.toLocaleString()}
                          </OfferPrice>
                        </OfferInfo>
                        <RemoveButton onClick={removeCurrentOffer} type="button">
                          ×
                        </RemoveButton>
                      </OfferAttachment>
                    )}

                    {compareList.length > 0 && (
                      <CompareAttachment
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <CompareInfo>
                          <CompareIcon>{compareList.length}</CompareIcon>
                          <CompareDetails>
                            <CompareTitle>{compareList.length} car{compareList.length > 1 ? 's' : ''} selected for comparison</CompareTitle>
                            <CompareSubtitle>
                              {compareList.length < 2 
                                ? "Select at least 2 cars to compare" 
                                : "Click \"Compare Now\" to see detailed comparison."
                              }
                            </CompareSubtitle>
                          </CompareDetails>
                        </CompareInfo>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <CompareButton 
                            type="button" 
                            onClick={handleCompareClick}
                            disabled={compareList.length < 2}
                          >
                            Compare Now
                          </CompareButton>
                          <RemoveButton onClick={clearCompareList} type="button">
                            <X size={16} />
                          </RemoveButton>
                        </div>
                      </CompareAttachment>
                    )}

                    {messages.length > 0 &&
                      isAppointmentCreated && !showFeedback ? <InfoMessage buttonText="Give Feedback" title="Your appointment has been scheduled!" onButtonClick={(e) => triggerFeedbackAfterAppointment(e)} /> :
                      isCreateAppointmentActive ?
                        <AppointmentBookingContainer
                          offerId={currentOffer?.offer?.id}
                          dealerId={selectedCar?.dealer_id}
                          onCancel={() => setIsCreateAppointmentActive(false)}
                          onSuccess={(data) => {
                            console.log(data);
                            // Format date and time for message
                            const appointmentDate = new Date(data.date);
                            const formattedDate = appointmentDate.toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric', 
                              month: 'long',
                              day: 'numeric'
                            });
                            const formattedTime = data.timeSlot.start;
                            
                            // Send confirmation message
                            if (socket && roomId) {
                              socket.emit('chatMessage', {
                                roomId: roomId,
                                message: `I have booked an appointment on ${formattedDate} at ${formattedTime} for ${selectedCar.year} ${selectedCar.make} ${selectedCar.model}`
                              });
                            }
                            setIsCreateAppointmentActive(false);
                            setIsAppointmentCreated(true);
                            
                          }}
                        />
                        : isCreateAccountActive ?
                          <AccountFormContainer
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <AccountFormTitle>
                              {isLoginMode ? 'Log In to Your Account' : 'Create Your Account'}
                            </AccountFormTitle>

                            {/* OAuth Login Options */}
                            <OAuthContainer>
                              <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleError}
                                useOneTap={false}
                                theme="outline"
                                size="large"
                                width="300"
                                text="signin_with"
                                shape="rectangular"
                                logo_alignment="left"
                                disabled={isOAuthLoading}
                              />
                              
                           
                            </OAuthContainer>

                            <OAuthDivider>
                              <span>Or continue with email</span>
                            </OAuthDivider>

                            {isLoginMode ? (
                              // Login Form
                              <AccountForm>
                                <div style={{ textAlign: 'center', marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.16px' }}>
                                  Log in to your existing account
                                </div>

                                {accountFormError && (
                                  <FormError>{accountFormError}</FormError>
                                )}

                                {accountFormSuccess && (
                                  <FormSuccess>{accountFormSuccess}</FormSuccess>
                                )}

                                <FormGroup>
                                  <FormLabel htmlFor="login-email">Email</FormLabel>
                                  <FormInput
                                    type="email"
                                    id="login-email"
                                    name="email"
                                    value={loginFormData.email}
                                    onChange={handleLoginFormInputChange}
                                    placeholder="Enter your email"
                                    required
                                  />
                                </FormGroup>

                                <FormGroup>
                                  <FormLabel htmlFor="login-password">Password</FormLabel>
                                  <FormInput
                                    type="password"
                                    id="login-password"
                                    name="password"
                                    value={loginFormData.password}
                                    onChange={handleLoginFormInputChange}
                                    placeholder="Enter your password"
                                    required
                                  />
                                </FormGroup>

                                <FormButtons>
                                  <CancelButton type="button" onClick={handleAccountFormCancel}>
                                    Cancel
                                  </CancelButton>
                                  <SubmitButton
                                    type="button"
                                    onClick={handleLoginFormSubmit}
                                    disabled={isAccountFormLoading}
                                  >
                                    {isAccountFormLoading ? 'Logging in...' : 'Log In'}
                                  </SubmitButton>
                                </FormButtons>

                                <ToggleButton type="button" onClick={toggleLoginMode}>
                                  Don't have an account? Create one
                                </ToggleButton>
                              </AccountForm>
                            ) : (
                              // Signup Form
                              <AccountForm>
                                {accountFormError && (
                                  <FormError>{accountFormError}</FormError>
                                )}

                                {accountFormSuccess && (
                                  <FormSuccess>{accountFormSuccess}</FormSuccess>
                                )}

                                <FormGroup>
                                  <FormLabel htmlFor="account-name">Full Name</FormLabel>
                                  <FormInput
                                    type="text"
                                    id="account-name"
                                    name="userInfo.name"
                                    value={accountFormData.userInfo.name}
                                    onChange={handleAccountFormInputChange}
                                    placeholder="Enter your full name"
                                    required
                                  />
                                </FormGroup>

                                <FormGroup>
                                  <FormLabel htmlFor="account-email">Email</FormLabel>
                                  <FormInput
                                    type="email"
                                    id="account-email"
                                    name="email"
                                    value={accountFormData.email}
                                    onChange={handleAccountFormInputChange}
                                    placeholder="Enter your email"
                                    required
                                  />
                                </FormGroup>

                                <FormRow>
                                  <FormGroup>
                                    <FormLabel htmlFor="account-phone">Phone</FormLabel>
                                    <FormInput
                                      type="tel"
                                      id="account-phone"
                                      name="userInfo.phone"
                                      value={accountFormData.userInfo.phone}
                                      onChange={handleAccountFormInputChange}
                                      placeholder="Enter phone number"
                                      required
                                    />
                                  </FormGroup>

                                  <FormGroup>
                                    <FormLabel htmlFor="account-city">City</FormLabel>
                                    <FormInput
                                      type="text"
                                      id="account-city"
                                      name="userInfo.city"
                                      value={accountFormData.userInfo.city}
                                      onChange={handleAccountFormInputChange}
                                      placeholder="Enter your city"
                                      required
                                    />
                                  </FormGroup>
                                </FormRow>

                                <FormGroup>
                                  <FormLabel htmlFor="account-address">Address</FormLabel>
                                  <FormInput
                                    type="text"
                                    id="account-address"
                                    name="userInfo.address"
                                    value={accountFormData.userInfo.address}
                                    onChange={handleAccountFormInputChange}
                                    placeholder="Enter your address"
                                    required
                                  />
                                </FormGroup>

                                <FormGroup>
                                  <FormLabel htmlFor="account-password">Password</FormLabel>
                                  <FormInput
                                    type="password"
                                    id="account-password"
                                    name="password"
                                    value={accountFormData.password}
                                    onChange={handleAccountFormInputChange}
                                    placeholder="Enter your password"
                                    required
                                  />
                                </FormGroup>

                                <FormGroup>
                                  <FormLabel htmlFor="account-confirm-password">Confirm Password</FormLabel>
                                  <FormInput
                                    type="password"
                                    id="account-confirm-password"
                                    name="confirmPassword"
                                    value={accountFormData.confirmPassword}
                                    onChange={handleAccountFormInputChange}
                                    placeholder="Confirm your password"
                                    required
                                  />
                                </FormGroup>

                                <FormButtons>
                                  <CancelButton type="button" onClick={handleAccountFormCancel}>
                                    Cancel
                                  </CancelButton>
                                  <SubmitButton
                                    type="button"
                                    disabled={isAccountFormLoading}
                                    onClick={handleAccountFormSubmit}
                                  >
                                    {isAccountFormLoading ? 'Creating Account...' : 'Create Account'}
                                  </SubmitButton>
                                </FormButtons>

                                <ToggleButton type="button" onClick={toggleLoginMode}>
                                  Already have an account? Log in
                                </ToggleButton>
                              </AccountForm>
                            )}
                          </AccountFormContainer>
                          : showFeedback ? (
                            <FeedbackForm
                              onSubmit={handleFeedbackSubmit}
                              onSkip={handleFeedbackSkip}
                              onClose={() => setShowFeedback(false)}
                              title="How was your experience?"
                              description="We'd love to hear about your car search experience. Your feedback helps us improve our AI assistant."
                            />
                          ) : <>
                            <Input
                              id="messageInput"
                              value={input}
                              onChange={handleInputChange}
                              onKeyDown={handleKeyPress}
                              disabled={isCreateAccountActive || showFeedback || isRequestingLocation || isWaitingForResponse}
                              placeholder={
                                isCreateAccountActive
                                  ? "Account creation in progress..."
                                  : showFeedback
                                    ? "Please provide feedback first..."
                                     : isRequestingLocation
                                       ? "Requesting location permission..."
                                     : isWaitingForResponse
                                       ? "Waiting for AI response..."
                                     : isVoiceMode
                                       ? "Voice mode active - speak to add text here..."
                                    : currentOffer
                                         ? "Ask about this offer or use voice input... (Shift+Enter for new line)"
                                      : selectedCar
                                           ? "Ask about this car or use voice input... (Shift+Enter for new line)"
                                           : "Write here what car do you want or use voice input... (Shift+Enter for new line)"
                              }
                            />
                            {/* Chat actions bar */}
                            <ChatActionsBar>
                              <SendButton
                                type="button"
                                onClick={handleClearClick}
                                aria-label="Clear chat"
                                disabled={isCreateAccountActive || showFeedback || isRequestingLocation || isWaitingForResponse}
                              >
                                <IconClear width={20} height={20} style={{ transform: 'scaleX(-1)' }} />
                              </SendButton>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <SendButton
                                   type="button"
                                   onClick={() => {
                                     console.log('🎤 Voice button clicked in chat actions');
                                     handleVoiceInput();
                                   }}
                                   style={{ 
                                     backgroundColor: isVoiceMode ? '#22C55E' : isVoiceSupported ? '#F43F60' : '#666666', 
                                     borderRadius: '50%', 
                                     padding: '10px', 
                                     width: '40px', 
                                     height: '40px',
                                     animation: isVoiceMode ? 'pulse 2s infinite' : 'none'
                                   }}
                                   disabled={isCreateAccountActive || showFeedback || isRequestingLocation || isWaitingForResponse || !isVoiceSupported}
                                   title={
                                     !isVoiceSupported 
                                       ? 'Voice input not supported in this browser' 
                                       : isVoiceMode 
                                         ? 'Voice mode active - Click to stop' 
                                         : 'Start voice mode'
                                   }
                                >
                                  <Mic />
                                </SendButton>
                                <SendButton
                                  type={(isCreateAccountActive || showFeedback || isRequestingLocation || isWaitingForResponse) ? "button" : "submit"}
                                  disabled={isCreateAccountActive || showFeedback || isRequestingLocation || isWaitingForResponse}
                                >
                                  <MoveUpRight />
                                </SendButton>
                              </div>
                            </ChatActionsBar>
                          </>}
                  </form>
                </ChatContainer>
                <div ref={messagesEndRef} />

                {/* <div className="cannames">
                  <div className="title">
                    <h2>Popular Make</h2>
                  </div>
                  <ul>
                    {['Toyota', 'Volkswagen', 'Hyundai', 'Honda', 'BMW', 'Ford', 'Tesla', 'Chrysler', 'Chevrolet', 'Lincoln', 'Mercedes-Benz', 'Nissan', 'Kia', 'Genesis', 'Cadillac', 'GMC', 'Buick', 'Rolls-Royce', 'Audi', 'Jeep', 'Dodge', 'Mazda', 'Ram', 'Ferrari'].map(make => (
                      <li key={make}><a href="/model">{make}</a></li>
                    ))}
                  </ul>
                </div> */}

              </div>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="footer-area">
        <div className="containers">
          <div className="row">
            <div className="col-md-6">
              <div className="copy">
                <p>Copyright © 2024 Automotiveai.com</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="trams">
                <p><a href="#">Privacy Policy</a> | <a href="#">Terms & Conditions</a></p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <a href="#" className="scrolltotop"><i className="bi bi-arrow-up-circle"></i></a>
        </div>
      </footer>

      {/* Clear Confirmation Modal */}
      <ConfirmationModal
        isOpen={showClearConfirmModal}
        onClose={() => setShowClearConfirmModal(false)}
        onConfirm={handleNewChat}
        title="Clear Chat History"
        message="Are you sure you want to clear all chat history and start a new conversation? This action cannot be undone."
        confirmText="Clear All"
        cancelText="Cancel"
      />
    </>
  );
};

export default HomeSearchPage;